import { ValidationRuleLevel } from "../DynamicFormTypes.ts";
import React from "react";
import { ArrowRightCircle } from "lucide-react";
import { ErrorFeedback, ErrorMessage, scrollToError } from "../dyanimcFormUtil.tsx";

interface ErrorLayoutProps {
    errorFeedback: ErrorFeedback;
}

export const FormToolbarErrorDisplayRenderer: React.FC<ErrorLayoutProps> = ({ errorFeedback }) => {
    if (!errorFeedback || errorFeedback.totalErrorsCount <= 0) {
        return null;
    }

    // Recursive component to render error messages with nesting
    const renderErrorMessage = (error: ErrorMessage) => {
        const errorId = `error-${error.fieldKey}`;
        const hasNested = error.nested && error.nested.length > 0;
        const level = error.level || ValidationRuleLevel.ERROR;
        const isWarning = level === ValidationRuleLevel.WARNING;
        const hasMessage = (error.message ?? "").trim().length > 0;

        // Define colors based on level
        const borderColor = isWarning ? 'border-yellow-300' : 'border-red-300';
        const hoverBgColor = isWarning ? 'hover:bg-yellow-100' : 'hover:bg-red-100';
        const textColor = isWarning ? 'text-yellow-600' : 'text-red-600';
        const textHoverColor = isWarning ? 'hover:text-yellow-800' : 'hover:text-red-800';
        const labelColor = isWarning ? 'text-yellow-700' : 'text-red-700';
        const buttonHoverBg = isWarning ? 'hover:bg-yellow-200' : 'hover:bg-red-200';

        return (
            <div key={errorId} className={`border-l-2 ${borderColor}`}>
                <div className={`flex ${hasMessage ? "items-start" : "items-center"} pl-2 ${hoverBgColor} p-2 rounded`}>
                    <div className="flex-1 cursor-pointer" onClick={() => scrollToError(error)}>
                        <p className={`text-sm font-medium ${labelColor}`}>{error.label}</p>
                        <p className={`text-sm ${textColor}`}>{error.message}</p>
                    </div>

                    <button
                        type="button"
                        onClick={() => scrollToError(error)}
                        className={`ml-2 p-1.5 flex-shrink-0 ${textColor} ${textHoverColor} ${buttonHoverBg} rounded-full transition-colors`}
                        title="Scroll to field"
                    >
                        <ArrowRightCircle className="h-5 w-5" />
                    </button>
                </div>

                {hasNested && (
                    <div className="ml-4">
                        {error.nested!.map(nestedError => renderErrorMessage(nestedError))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="space-y-2 max-h-60 overflow-y-auto">
                {errorFeedback.errors.map(error => renderErrorMessage(error))}
                {errorFeedback.warnings.map(warning => renderErrorMessage(warning))}
            </div>
        </>

    );
};

export const FormToolbarErrorDisplay = React.memo(FormToolbarErrorDisplayRenderer);